import React from 'react';
import { observer } from 'mobx-react';
import { Checkbox } from '@components/checkbox';
import { FormSection } from '@components/form-section';
import { ImageUpload } from '@components/image-upload';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { useCreateCommunityStore } from './create-community-store';
import {
    CommunityType,
    CommunityAccessLevel,
    ChatNotificationSelection,
    LocaleCode
} from '@api/ChallengeClient';

const enumOptions = <T extends string>(values: T[]) =>
    values.map((value) => ({ label: value, value }));

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

export const CreateCommunity = observer(() => {
    const {
        fields,
        onSubmitHandler,
        createRequest,
        onCancelHandler,
        imageUpload
    } = useCreateCommunityStore();

    return (
        <>
            <PageHeader title="Create New Community" />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection title="Basic Information">
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            label="Community Name"
                            type="text"
                            value={fields.name.value}
                            onChange={(value) =>
                                fields.name.onChange(String(value))
                            }
                            error={fields.name.error}
                            required
                        />
                        <Select
                            label="Community Type"
                            required
                            value={fields.type.value}
                            error={fields.type.error}
                            options={enumOptions(Object.values(CommunityType))}
                            onChange={(value: CommunityType) =>
                                fields.type.onChange(value)
                            }
                        />
                        <TextField
                            label="Tenant ID"
                            type="text"
                            value={fields.tenantId.value ?? ''}
                            onChange={(value) =>
                                fields.tenantId.onChange(String(value))
                            }
                            error={fields.tenantId.error}
                        />
                        <Select
                            label="Locale"
                            required
                            value={fields.locale.value}
                            error={fields.locale.error}
                            options={enumOptions(Object.values(LocaleCode))}
                            onChange={(value: LocaleCode) =>
                                fields.locale.onChange(value)
                            }
                        />
                    </div>
                </FormSection>

                <FormSection title="Description">
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            label="Description (English)"
                            type="text"
                            value={fields.description.en.value}
                            onChange={(value) =>
                                fields.description.en.onChange(String(value))
                            }
                            multiline
                        />
                        <TextField
                            label="Description (German)"
                            type="text"
                            value={fields.description.de.value}
                            onChange={(value) =>
                                fields.description.de.onChange(String(value))
                            }
                            multiline
                        />
                        <TextField
                            label="Description (Polish)"
                            type="text"
                            value={fields.description.pl.value}
                            onChange={(value) =>
                                fields.description.pl.onChange(String(value))
                            }
                            multiline
                        />
                    </div>
                </FormSection>

                <FormSection title="Photo Settings">
                    <div className="flex flex-col gap-4">
                        <ImageUpload
                            onUpload={imageUpload.handleUpload}
                            loading={imageUpload.loading}
                            error={imageUpload.error}
                            previewUrl={imageUpload.previewUrl}
                            label="Community Photo"
                            helperText="Drag and drop a community photo here, or click to select"
                        />
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Photo Bucket Name"
                                    type="text"
                                    value={fields.photo.bucketName.value}
                                    onChange={(value) =>
                                        fields.photo.bucketName.onChange(
                                            String(value)
                                        )
                                    }
                                    error={fields.photo.bucketName.error}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Photo Object Key"
                                    type="text"
                                    value={fields.photo.objectKey.value}
                                    onChange={(value) =>
                                        fields.photo.objectKey.onChange(
                                            String(value)
                                        )
                                    }
                                    error={fields.photo.objectKey.error}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Manager Settings">
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Manager User IDs (comma-separated)"
                            type="text"
                            value={fields.managerUserIds.value}
                            onChange={(value) =>
                                fields.managerUserIds.onChange(String(value))
                            }
                        />
                        <Hint>Enter user IDs separated by commas</Hint>
                    </div>
                </FormSection>

                <FormSection title="Access Settings">
                    <div className="grid gap-4 md:grid-cols-3">
                        <Select
                            label="Member Invitation"
                            required
                            value={fields.memberInvitation.value}
                            error={fields.memberInvitation.error}
                            options={enumOptions(
                                Object.values(CommunityAccessLevel)
                            )}
                            onChange={(value: CommunityAccessLevel) =>
                                fields.memberInvitation.onChange(value)
                            }
                        />
                        <Select
                            label="Event Creation"
                            required
                            value={fields.eventCreation.value}
                            error={fields.eventCreation.error}
                            options={enumOptions(
                                Object.values(CommunityAccessLevel)
                            )}
                            onChange={(value: CommunityAccessLevel) =>
                                fields.eventCreation.onChange(value)
                            }
                        />
                        <Select
                            label="Chat Notifications"
                            required
                            value={fields.notificationsChat.value}
                            error={fields.notificationsChat.error}
                            options={enumOptions(
                                Object.values(ChatNotificationSelection)
                            )}
                            onChange={(value: ChatNotificationSelection) =>
                                fields.notificationsChat.onChange(value)
                            }
                        />
                    </div>
                </FormSection>

                <FormSection title="Community Settings">
                    <div className="grid gap-4 md:grid-cols-3">
                        <Checkbox
                            center
                            checked={fields.tenantMemberAutoJoinEnabled.value}
                            onChange={(_, checked) =>
                                fields.tenantMemberAutoJoinEnabled.onChange(
                                    checked
                                )
                            }
                        >
                            Tenant Member Auto-Join Enabled
                        </Checkbox>
                        <Checkbox
                            center
                            checked={fields.leaveEnabled.value}
                            onChange={(_, checked) =>
                                fields.leaveEnabled.onChange(checked)
                            }
                        >
                            Leave Enabled
                        </Checkbox>
                        <Checkbox
                            center
                            checked={fields.initMembers.value}
                            onChange={(_, checked) =>
                                fields.initMembers.onChange(checked)
                            }
                        >
                            Initialize Members
                        </Checkbox>
                    </div>
                </FormSection>

                {createRequest.error && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {createRequest.error.message}
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={onCancelHandler}
                    disabled={createRequest.loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onSubmitHandler}
                    loading={createRequest.loading}
                >
                    Create Community
                </Button>
            </StickyBar>
        </>
    );
});
